import {
  type ICreateOfferPayload,
  type ILlmExtractJobOffer,
  type IOfferPayloadEnterprise,
  type IPutJobOfferPayload,
  useGetEnterprise,
  useGetOfferUrl,
  usePostDataOffer,
  usePostOfferPinned,
  usePutDataOffer,
} from "@api";
import type { IFormGenContactsLookup } from "@artifact-management/json-dynamic-form";
import type { IContact } from "@bbj-types/contact";
import type { IOffer, TContractDurationUnit } from "@bbj-types/offer";
import type { TEnterpriseOption } from "@complex/FormGen/Components/Inputs/Enterprise";
import { useGlobalContext } from "@root/context";
import type { FilteredSettings } from "@root/contextLogic";
import { toStartOfDayISO } from "@root/utils/date/toStartOfDayISO";
import { InternalOfferSource, isInternalOfferSource } from "@root/utils/enums/offer";
import { useI18nContext } from "@root/utils/organisation/i18nContext";
import { useMemo, useRef } from "react";
import type { FieldValues } from "react-hook-form";
import type { TOfferForm } from ".";
import { formatContactFromExtractedOffer } from "../logics";
import { offerJsonSchema } from "./schema";

export const parseOfferFromAI =
  (phoneCode: string) =>
  (item: ILlmExtractJobOffer): FieldValues => {
    const isCdi = item.contractType?.[0] === "cdi";
    return {
      name: item.name,
      description: item.description,
      url: item.url,
      contractType: item.contractType?.[0],
      isPermanent: item.isPermanent,
      startDate: item.startDate,
      expirationDate: item.endDate,
      workWeeklyDuration: item.workWeeklyDuration,
      ...(item.address ? { address: item.address } : {}),
      ...(!isCdi && item.contractDuration
        ? {
            contractDurationValue: item.contractDuration.duration,
            contractDurationUnit: item.contractDuration.unit ?? "month",
          }
        : {}),
      ...(item.contact ? { contact: formatContactFromExtractedOffer(item.contact, phoneCode) } : {}),
      ...(item.enterprise?._id && item.enterprise.name
        ? { enterprise: { id: item.enterprise._id, label: item.enterprise.name } }
        : {}),
    };
  };

const formatOfferFromAI = (item: TOfferForm, timeZone: string): ICreateOfferPayload["offer"] => {
  const isCdi = item.contractType.value === "cdi";
  return {
    name: item.name,
    url: item.url,
    address: item.address,
    contractType: [item.contractType.value],
    contractDuration:
      !isCdi && item.contractDurationUnit && item.contractDurationValue
        ? { unit: item.contractDurationUnit.value as TContractDurationUnit, duration: item.contractDurationValue }
        : undefined,
    startDate: toStartOfDayISO(item.startDate, timeZone),
    expirationDate: item.isPermanent ? undefined : toStartOfDayISO(item.expirationDate, timeZone),
    isPermanent: item.isPermanent,
    appellationCode: item.appellation?.id,
    appellationLabel: item.appellation?.label,
    romeCode: item.appellation?.romeCode,
    romeLabel: item.appellation?.romeLabel,
    workWeeklyDuration: item.workWeeklyDuration,
    description: item.description,
    externalSource: item.externalSource?.value ?? "",
  };
};

const formatPutOfferFromAI = (
  item: TOfferForm,
  matched: IOffer,
  editorId: string,
  timeZone: string
): IPutJobOfferPayload["offer"] => {
  const { enterprise, contact, metadata, startDate, expirationDate, ...rest } = matched;
  return {
    ...rest,
    ...formatOfferFromAI(item, timeZone),
    publicationUpdatedDate: new Date().toISOString(),
    lastEditorId: editorId,
  };
};

const formatEnterpriseFromAI = (item: TOfferForm): IOfferPayloadEnterprise | undefined =>
  item.enterprise ? { _id: item.enterprise.id } : undefined;

const formatContactFromAI = (item: TOfferForm): Partial<IContact> | undefined =>
  item.contact &&
  (item.contact._id || item.contact.firstName || item.contact.lastName || item.contact.email || item.contact.phone)
    ? {
        ...(item.contact._id ? { _id: item.contact._id } : {}),
        firstName: item.contact.firstName,
        lastName: item.contact.lastName,
        job: item.contact.job,
        email: item.contact.email,
        phone: item.contact.phone,
        place: item.contact.place,
        description: item.contact.description,
      }
    : undefined;

export const useEnterpriseContactsLookup = (): IFormGenContactsLookup => {
  const [, getEnterprise] = useGetEnterprise();

  return useMemo(
    () => ({
      fetch: async (enterprise) => {
        const id = (enterprise as TEnterpriseOption | null)?.id;
        if (!id) {
          return [];
        }
        const result = await getEnterprise(id);
        return (result.data.contacts ?? []).map((contact) => ({
          _id: String(contact._id),
          firstName: contact.firstName,
          lastName: contact.lastName,
          job: contact.job,
          email: contact.email,
          phone: contact.phone,
          place: contact.place,
          description: contact.description,
        }));
      },
    }),
    [getEnterprise]
  );
};

export const useOfferUrlLookup = (settingsV2: FilteredSettings) => {
  const [, getOfferUrl, cancel] = useGetOfferUrl();
  const matchedOffersRef = useRef<Map<string, IOffer>>(new Map());

  const fetch = async (value: string): Promise<Record<string, unknown> | null> => {
    const result = await getOfferUrl(value);
    if (result.status !== 200 || !result.data.data) {
      matchedOffersRef.current.delete(value);
      return null;
    }
    const offer = result.data.data;
    matchedOffersRef.current.set(value, offer);
    const contractTypeOption = settingsV2.settingsContractTypes?.values.find(
      (option) => option.value === offer.contractType?.[0]
    );

    const externalSourceOption = settingsV2.settingsOfferOrigin?.values.find(
      (option) => option.value === offer.externalSource
    );
    const contractDurationUnitOption = offerJsonSchema.properties.contractDurationUnit.enum.find(
      (option) => option.value === offer.contractDuration?.unit
    );

    const overrides: Record<string, unknown> = {
      name: offer.name,
      isPermanent: offer.isPermanent,
      address: offer.address,
      startDate: offer.startDate,
      expirationDate: offer.expirationDate,
      description: offer.description,
      workWeeklyDuration: offer.workWeeklyDuration,
      isOfferLocked: !isInternalOfferSource(offer.source),
    };
    if (contractTypeOption) {
      overrides.contractType = { value: contractTypeOption.value, label: contractTypeOption.label };
    }
    if (externalSourceOption) {
      overrides.externalSource = { value: externalSourceOption.value, label: externalSourceOption.label };
    }
    if (contractDurationUnitOption && offer.contractDuration) {
      overrides.contractDurationValue = offer.contractDuration.duration;
      overrides.contractDurationUnit = contractDurationUnitOption;
    }
    if (offer.enterprise?._id) {
      overrides.enterprise = { id: String(offer.enterprise._id), label: offer.enterprise.name };
    }
    if (offer.contact) {
      overrides.contact = offer.contact;
    }
    if (offer.appellationCode) {
      overrides.appellation = {
        id: offer.appellationCode,
        label: offer.appellationLabel,
        romeCode: offer.romeCode,
        romeLabel: offer.romeLabel,
      };
    }
    return overrides;
  };

  const getMatchedOffer = (url: string | undefined): IOffer | undefined =>
    url ? matchedOffersRef.current.get(url) : undefined;

  return { urlContext: { url: { fetch, cancel, resetOverrides: { isOfferLocked: false } } }, getMatchedOffer };
};

export const useCreateAndPinOffers = (
  candidateId: string | undefined,
  getMatchedOffer: (url: string | undefined) => IOffer | undefined
) => {
  const { user } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const [{ loading: loadingPost }, postOffer] = usePostDataOffer();
  const [{ loading: loadingPut }, putOffer] = usePutDataOffer();
  const [{ loading: loadingPin }, pinOffer] = usePostOfferPinned();

  const createAndPinOffers = async (items: TOfferForm[]) => {
    const created: IOffer[] = [];
    const failed: TOfferForm[] = [];
    const failedIndexes: number[] = [];
    for (const [index, item] of items.entries()) {
      const matched = candidateId ? getMatchedOffer(item.url) : undefined;
      try {
        if (matched && isInternalOfferSource(matched.source)) {
          const putPayload = formatPutOfferFromAI(item, matched, user._id, timeZone);
          await putOffer(matched.offerId, {
            offer: putPayload,
            enterprise: formatEnterpriseFromAI(item) ?? {},
            contact: formatContactFromAI(item),
          });
          if (!matched.metadata?.starredAt) {
            await pinOffer(candidateId as string, matched.offerId);
          }
          created.push({ ...matched, ...putPayload });
        } else if (matched) {
          await pinOffer(candidateId as string, matched.offerId);
          created.push(matched);
        } else {
          const result = await postOffer({
            offer: { ...formatOfferFromAI(item, timeZone), source: InternalOfferSource.BBJ_ASSISTANT },
            enterprise: formatEnterpriseFromAI(item),
            contact: formatContactFromAI(item),
            starCandidate: candidateId,
          });
          if (result.status === 201 && result.data) {
            created.push(result.data);
          } else {
            failed.push(item);
            failedIndexes.push(index);
          }
        }
      } catch {
        failed.push(item);
        failedIndexes.push(index);
      }
    }
    return { created, failed, failedIndexes };
  };

  return { createAndPinOffers, loading: loadingPost || loadingPut || loadingPin };
};
